import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { getDemo, endDemo } from "../lib/api";
import { toast } from "sonner";
import EndDemoSummary from "../components/EndDemoSummary";
import CartesiaCall from "../components/CartesiaCall";
import ChatTryout from "../components/ChatTryout";
import VoiceGenderSelector from "../components/VoiceGenderSelector";

/**
 * Pagina Demo "telefonata-only".
 * Il cliente atterra qui dopo l'analisi del sito di Lara.
 * Vede solo: header con azienda + CTA gigante arancione "Telefonata reale" + "Termina e ricevi riassunto".
 * Nessuna chat scritta. La voce è SOLO quella OpenAI Realtime (Cedar/Marin) durante la telefonata.
 */
export default function Demo() {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [endingDemo, setEndingDemo] = useState(false);
  const [summary, setSummary] = useState(null);
  const [realtimeOpen, setRealtimeOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedGender, setSelectedGender] = useState(null); // "M" | "F" | null
  // Tiene traccia delle POST di trascrizione in volo, per attenderle prima di chiudere
  const pendingTranscriptsRef = useRef([]);
  // Evita doppi end_demo (es. doppio hangup / re-render)
  const endTriggeredRef = useRef(false);

  const handleGenderSelect = (gender) => {
    setSelectedGender(gender);
    endTriggeredRef.current = false;
    setRealtimeOpen(true);
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await getDemo(sessionId);
        setSession(data.session);
        setMessages(data.messages || []);
      } catch (e) {
        toast.error("Sessione non trovata o scaduta");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [sessionId]);

  // Quando l'utente riaggancia, generiamo SUBITO il rapporto automaticamente.
  const handleCallClosed = async () => {
    // 1. Mostra immediatamente il loader (evita flash di pagina vuota)
    if (endTriggeredRef.current) {
      setRealtimeOpen(false);
      return;
    }
    endTriggeredRef.current = true;
    setEndingDemo(true);
    setRealtimeOpen(false);

    // 2. Aspetta che tutte le POST di trascrizione in volo siano scritte sul DB
    try {
      await Promise.allSettled(pendingTranscriptsRef.current);
    } catch {}
    pendingTranscriptsRef.current = [];

    // 3. Genera il rapporto (backend invia anche email + WhatsApp dentro notify_owner)
    try {
      const res = await endDemo(sessionId);
      setSummary(res.summary);
    } catch (e) {
      toast.error("Errore nella generazione del rapporto");
    } finally {
      setEndingDemo(false);
    }
  };

  const handleCloseSummary = () => setSummary(null);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg">
        <Loader2 className="w-8 h-8 animate-spin text-brand-olive" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg p-8">
        <div className="text-center max-w-md">
          <h2 className="font-heading text-3xl mb-4">Sessione non trovata</h2>
          <Link to="/" className="text-brand-olive underline" data-testid="back-home-link">
            Torna alla home
          </Link>
        </div>
      </div>
    );
  }

  const companyName = session.company?.nome || session.website || "la tua azienda";

  return (
    <div data-testid="demo-page" className="min-h-screen bg-brand-bg flex flex-col">
      {/* Header */}
      <header className="border-b border-brand-line bg-white/60 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-brand-inkSoft hover:text-brand-ink transition"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Indietro</span>
          </Link>
          <div className="flex flex-col items-center">
            <span className="font-heading text-xl text-brand-ink">{companyName}</span>
            <span className="text-xs text-brand-inkSoft">demo 24Vox · voce reale</span>
          </div>
          <div className="w-16" /> {/* spacer per bilanciare il layout */}
        </div>
      </header>

      {/* Sezione centrale: selettore voce + CTA telefonata */}
      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-xl text-center">
          {/* Sottotitolo / istruzione */}
          <p
            className="text-base sm:text-lg text-brand-inkSoft mb-10 leading-relaxed"
            data-testid="demo-call-instruction"
          >
            La voce di <span className="font-semibold text-brand-ink">{companyName}</span> al telefono.
            Scegli prima quale userà a rispondere.
          </p>

          {/* Selettore voce: scelta obbligatoria */}
          <VoiceGenderSelector onSelect={handleGenderSelect} />

          <p className="mt-10 text-xs text-brand-inkSoft">
            Quando riagganci, Lara ti invia subito il rapporto della chiamata.
          </p>
        </div>
      </main>

      {/* Modal summary fine demo */}
      {(summary || endingDemo) && (
        <EndDemoSummary
          summary={summary}
          sessionInfo={session}
          loading={endingDemo}
          onClose={handleCloseSummary}
          onTryChat={() => setChatOpen(true)}
        />
      )}

      {/* Bolla chat di prova (lo stesso assistente in formato chatbot per il sito) */}
      {chatOpen && (
        <ChatTryout
          sessionId={sessionId}
          companyName={companyName}
          greeting={session?.greeting}
          onClose={() => setChatOpen(false)}
        />
      )}

      {/* Modal telefonata voce reale */}
      {realtimeOpen && (
        <CartesiaCall
          sessionId={sessionId}
          companyName={companyName}
          gender={selectedGender}
          onClose={handleCallClosed}
          onTranscriptUpdate={(msg) => {
            setMessages((prev) => [
              ...prev,
              { id: `rt-${Date.now()}-${Math.random()}`, ...msg },
            ]);
            // Salva anche nel DB per il report WhatsApp finale
            if (msg && msg.role && msg.content) {
              const backend = process.env.REACT_APP_BACKEND_URL || "";
              const p = fetch(`${backend}/api/demo/${sessionId}/cartesia/transcript`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ role: msg.role, content: msg.content }),
                keepalive: true,
              }).catch(() => {});
              pendingTranscriptsRef.current.push(p);
            }
          }}
        />
      )}
    </div>
  );
}
